// Predkontrola dokumentu v prehliadači pred uploadom — počet strán a jazyk (23. 9. 2026).
//
// - Najviac PAGE_LIMIT strán (50); 23. 9. 2026 dokument s 1 839 stranami
//   zhodil engine. Superuser má výnimku (testovanie).
// - Jazyk: iný ako slovenský alebo neurčený → otázka „Chcete pokračovať?“.
// - PDF bez textovej vrstvy (sken) sa tu určiť nedá — jazyk overí server
//   (OCR vzorky) a vráti 409 `language_confirm`, na ktorý sa pýtame tiež.
// Každé porušenie aj rozhodnutie používateľa ide do Vigilu cez backend.
import * as pdfjs from 'pdfjs-dist/build/pdf';
import JSZip from 'jszip';
import { detectLanguage } from './langDetect';

export const PAGE_LIMIT = Number(process.env.REACT_APP_MAX_PAGES || 50);
const API_BASE_URL = process.env.REACT_APP_API_ADDRESS;
const TEXT_WORDS = 3000;
const MIN_TEXT_WORDS = 25;

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/static/pdfjs/3.4.120/pdf.worker.min.js`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

let superuserPromise = null;
export const isSuperuser = () => {
  if (!superuserPromise) {
    superuserPromise = fetch(`${API_BASE_URL}/accounts/users/user_info/`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : {}))
      .then((u) => !!(u?.is_superuser || u?.user?.is_superuser))
      .catch(() => false);
  }
  return superuserPromise;
};

// Porušenie / rozhodnutie do Vigilu — bez obsahu dokumentu, len metadáta.
export const reportPrecheckEvent = (payload) => {
  try {
    fetch(`${API_BASE_URL}/anonymization/pii/precheck-event/`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch (e) { /* hlásenie nesmie zastaviť prácu */ }
};

const pdfInfo = async (file) => {
  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data, isEvalSupported: false }).promise;
  try {
    const pages = doc.numPages;
    if (pages > PAGE_LIMIT) return { pages, method: null, lang: null };
    const words = [];
    for (let i = 1; i <= pages && words.length < TEXT_WORDS; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const page = await doc.getPage(i);
      // eslint-disable-next-line no-await-in-loop
      const content = await page.getTextContent();
      content.items.forEach((it) => { if (it.str) words.push(...it.str.split(/\s+/).filter(Boolean)); });
    }
    if (words.length < MIN_TEXT_WORDS) return { pages, method: 'needs_server', lang: null };
    return { pages, method: 'text', lang: detectLanguage(words.join(' ')) };
  } finally {
    doc.destroy();
  }
};

const docxInfo = async (file) => {
  const zip = await JSZip.loadAsync(file);
  let pages = null;
  const app = zip.file('docProps/app.xml');
  if (app) {
    const m = /<Pages>(\d+)<\/Pages>/.exec(await app.async('string'));
    if (m) pages = Number(m[1]);
  }
  const docXml = zip.file('word/document.xml');
  const xml = docXml ? await docXml.async('string') : '';
  const text = xml.replace(/<\/w:p>/g, '\n').replace(/<[^>]+>/g, '');
  if (!pages) pages = Math.max(1, Math.ceil(text.length / 3000));
  return { pages, method: 'text', lang: detectLanguage(text) };
};

// { pages, method: 'text' | 'needs_server' | null, lang } alebo null pri chybe čítania
export const inspectFile = async (file) => {
  const name = (file?.name || '').toLowerCase();
  try {
    if (name.endsWith('.pdf')) return await pdfInfo(file);
    if (name.endsWith('.docx')) return await docxInfo(file);
  } catch (e) { /* poškodený alebo zaheslovaný súbor — rozhodne server */ }
  return null;
};

export const pagesMessage = (name, pages) =>
  `Dokument „${name}“ má ${pages} strán. Anonymizovať môžeme dokument s najviac ${PAGE_LIMIT} stranami. Rozdeľte ho na menšie časti a nahrajte znova.`;

export const languageText = (lang) =>
  (!lang || lang.lang === 'undetermined'
    ? 'jazyk sa nepodarilo určiť'
    : `pravdepodobne ${lang.name || lang.lang}`);

// Rozdelí súbory: ok / tooManyPages / needConfirm (jazyk). Porušenie strán hlási do Vigilu.
export const precheckFiles = async (files, op) => {
  const su = await isSuperuser();
  const ok = [];
  const tooManyPages = [];
  const needConfirm = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const file of files) {
    // eslint-disable-next-line no-await-in-loop
    const info = await inspectFile(file);
    if (info && info.pages > PAGE_LIMIT && !su) {
      tooManyPages.push({ file, pages: info.pages });
      reportPrecheckEvent({ event: 'too_many_pages', op, filename: file.name, pages: info.pages, limit: PAGE_LIMIT, size_bytes: file.size });
    } else if (info && info.lang && !info.lang.accepted) {
      needConfirm.push({ file, lang: info.lang, method: info.method });
      reportPrecheckEvent({ event: 'language_confirm_required', op, filename: file.name,
        lang: info.lang.lang, sk_share: info.lang.sk_share, method: info.method });
    } else {
      ok.push(file);
    }
  }
  return { ok, tooManyPages, needConfirm };
};

// Po odpovedi dialógu: pri súhlase zaznač potvrdenie na File (služby pošlú
// confirm_language=1 a server zapíše language_override do Vigilu), pri zrušení
// to nahlás sem — súbor sa na server už nedostane.
export const resolveLanguage = (items, op, continued) => {
  items.forEach(({ file, lang, method }) => {
    if (continued) {
      file.cenzorLangConfirmed = true; // eslint-disable-line no-param-reassign
    } else {
      reportPrecheckEvent({ event: 'language_cancelled', op, filename: file.name,
        lang: lang?.lang, sk_share: lang?.sk_share, method: method || 'text' });
    }
  });
};

// Odpoveď servera 409 language_confirm (skeny — jazyk určilo OCR) → položka pre dialóg.
export const serverLanguageItem = (file, info) => ({
  file, method: 'ocr',
  lang: { lang: info?.lang, name: info?.name, sk_share: info?.sk_share },
});

export const cancelledMessage = (names) =>
  `Dokumenty v inom jazyku sme nepridali: ${names.join(', ')}.`;
